import React, { useEffect, useState } from 'react';
import useApi from '../api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DirectoryUser } from '../services/servicesShared';

export const directoryUserLabel = (user: DirectoryUser): string =>
    user.display_name || user.email || user.external_id || 'Unnamed user';

/**
 * Pick a customer user (GET /jobs/directory?q=): the people who have
 * identified in the vendor's product. Used to assign a job and to change an
 * owner.
 */
export const DirectorySearch: React.FC<{
    selected: DirectoryUser | null;
    onSelect: (user: DirectoryUser | null) => void;
    autoFocus?: boolean;
}> = ({ selected, onSelect, autoFocus }) => {
    const { get } = useApi();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<DirectoryUser[]>([]);
    const [searching, setSearching] = useState(false);

    useEffect(() => {
        if (!query.trim()) {
            setResults([]);
            return;
        }
        const handle = setTimeout(async () => {
            try {
                setSearching(true);
                const response = await get(`/jobs/directory?q=${encodeURIComponent(query.trim())}`);
                setResults(response.data?.data || []);
            } catch (error) {
                console.error('Error searching the customer directory:', error);
            } finally {
                setSearching(false);
            }
        }, 300);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query]);

    if (selected) {
        return (
            <div className="flex items-center justify-between rounded-md border px-3 py-2">
                <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{directoryUserLabel(selected)}</div>
                    <div className="text-xs text-muted-foreground truncate">
                        {[selected.email, selected.external_id && `id ${selected.external_id}`].filter(Boolean).join(' · ') || '—'}
                    </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => onSelect(null)}>
                    Change
                </Button>
            </div>
        );
    }

    return (
        <div className="space-y-1">
            <Input
                autoFocus={autoFocus}
                placeholder="Search customers by name, email or external id…"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
            />
            {searching && <p className="text-xs text-muted-foreground">Searching…</p>}
            {!searching && Boolean(results.length) && (
                <div className="rounded-md border divide-y max-h-56 overflow-y-auto scrollbar-thin">
                    {results.map((user) => (
                        <button
                            key={user.id}
                            type="button"
                            className="w-full text-left px-3 py-2 hover:bg-muted focus-visible:bg-muted outline-none"
                            onClick={() => {
                                onSelect(user);
                                setQuery('');
                                setResults([]);
                            }}
                        >
                            <div className="text-sm font-medium truncate">{directoryUserLabel(user)}</div>
                            <div className="text-xs text-muted-foreground truncate">
                                {[user.email, user.tenants.length ? `${user.tenants.length} workspace${user.tenants.length === 1 ? '' : 's'}` : null]
                                    .filter(Boolean)
                                    .join(' · ') || '—'}
                            </div>
                        </button>
                    ))}
                </div>
            )}
            {!searching && query.trim() && !results.length && (
                <p className="text-xs text-muted-foreground">
                    No one matches. The person must have identified in your product first.
                </p>
            )}
        </div>
    );
};

export default DirectorySearch;
